import { useEffect, useState } from "react";
import { CalendarClock, MapPin, ShoppingBag, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const DATE_FORMAT = {
  weekday: "short",
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "Asia/Kolkata",
};

function formatSlotDate(slotDate) {
  if (!slotDate) {
    return "Date unavailable";
  }
  const date = new Date(`${slotDate}T00:00:00+05:30`);
  if (Number.isNaN(date.getTime())) {
    return slotDate;
  }
  return date.toLocaleDateString("en-IN", DATE_FORMAT);
}

function formatSlotTime(slotTime) {
  if (!slotTime || slotTime === "ASAP") {
    return slotTime || "Time unavailable";
  }
  const [hours, minutes] = slotTime.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return slotTime;
  }
  const date = new Date(2000, 0, 1, hours, minutes);
  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function deliveryAddress(customer) {
  const fields = ["address_line1", "address_line2", "area", "city", "pincode"];
  return fields.map((field) => customer?.[field]).filter(Boolean).join(", ") || "Not provided";
}

export default function OrderDetailsModal({ order, onClose, onUpdated }) {
  const [deliveryFee, setDeliveryFee] = useState("");
  const [savingDeliveryFee, setSavingDeliveryFee] = useState(false);
  const [actualDeliveryCost, setActualDeliveryCost] = useState("");
  const [savingActualDeliveryCost, setSavingActualDeliveryCost] = useState(false);

  useEffect(() => {
    setDeliveryFee(Number(order?.delivery_fee || 0).toFixed(2));
    const currentCost = order?.actual_delivery_cost_manual
      ?? order?.profit?.actual_delivery_cost;
    setActualDeliveryCost(
      currentCost === null || currentCost === undefined
        ? ""
        : Number(currentCost).toFixed(2),
    );
  }, [order]);

  if (!order) {
    return null;
  }

  const isDelivery = order.fulfillment === "delivery";
  const slotLabel = isDelivery ? "Chosen delivery slot" : "Chosen pickup slot";
  const saveDeliveryFee = async () => {
    const nextFee = Number(deliveryFee);
    if (!Number.isFinite(nextFee) || nextFee < 0) {
      toast.error("Enter a valid delivery charge.");
      return;
    }
    setSavingDeliveryFee(true);
    try {
      const { data } = await api.put(`/admin/orders/${order.id}/delivery-fee`, {
        delivery_fee: nextFee,
      });
      onUpdated(data.order);
      toast.success("Delivery charge updated.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update the delivery charge.");
    } finally {
      setSavingDeliveryFee(false);
    }
  };
  const saveActualDeliveryCost = async () => {
    const nextCost = Number(actualDeliveryCost);
    if (!Number.isFinite(nextCost) || nextCost < 0) {
      toast.error("Enter a valid estimated cost.");
      return;
    }
    setSavingActualDeliveryCost(true);
    try {
      const { data } = await api.put(`/admin/orders/${order.id}/actual-delivery-cost`, {
        actual_delivery_cost: nextCost,
      });
      onUpdated(data.order);
      toast.success("Estimated delivery cost updated.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update the estimated cost.");
    } finally {
      setSavingActualDeliveryCost(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-details-title"
      data-testid={`order-details-modal-${order.order_number}`}
      className={
        "fixed inset-0 z-[90] flex items-end justify-center bg-espresso/65 p-0 "
        + "backdrop-blur-sm sm:items-center sm:p-6"
      }
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="max-h-[92vh] w-full overflow-y-auto border border-line bg-ivory sm:max-w-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-line p-5 sm:p-7">
          <div>
            <div className="label-xs text-gold-hover">Order details</div>
            <h2 id="order-details-title" className="mt-2 font-serif text-3xl text-espresso">
              {order.order_number}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close order details"
            title="Close order details"
            data-testid={`order-details-close-${order.order_number}`}
            className={
              "flex h-10 w-10 items-center justify-center border border-line text-espresso "
              + "transition-colors duration-200 hover:bg-espresso hover:text-ivory"
            }
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="space-y-6 p-5 sm:p-7">
          <section
            data-testid={`order-details-slot-${order.order_number}`}
            className="border-l-4 border-gold bg-gold/10 px-5 py-4"
          >
            <div className="flex items-center gap-2 label-xs text-gold-hover">
              <CalendarClock size={15} aria-hidden="true" />
              {slotLabel}
            </div>
            <p
              data-testid={`order-details-slot-value-${order.order_number}`}
              className="mt-2 font-serif text-2xl text-espresso sm:text-3xl"
            >
              {formatSlotDate(order.slot_date)} · {formatSlotTime(order.slot_time)}
            </p>
            <p className="mt-1 text-xs text-espresso-light">
              India Standard Time · {order.fulfillment}
            </p>
          </section>

          <div className="grid gap-5 sm:grid-cols-2">
            <section>
              <div className="flex items-center gap-2 label-xs text-espresso-light">
                <UserRound size={14} aria-hidden="true" />
                Customer
              </div>
              <p
                data-testid={`order-details-customer-${order.order_number}`}
                className="mt-2 text-sm text-espresso"
              >
                {order.customer?.full_name || "Customer"}
              </p>
              <p className="mt-1 text-xs text-espresso-light">{order.customer?.mobile || "—"}</p>
            </section>
            {isDelivery && (
              <section>
                <div className="flex items-center gap-2 label-xs text-espresso-light">
                  <MapPin size={14} aria-hidden="true" />
                  Delivery address
                </div>
                <p
                  data-testid={`order-details-address-${order.order_number}`}
                  className="mt-2 text-sm leading-relaxed text-espresso"
                >
                  {deliveryAddress(order.customer)}
                </p>
              </section>
            )}
          </div>

          {isDelivery && (
            <section className="border-t border-line pt-5">
              <label
                htmlFor={`delivery-fee-${order.order_number}`}
                className="label-xs text-espresso-light"
              >
                Delivery charge
              </label>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="w-full sm:max-w-[180px]">
                  <div className="relative">
                    <span
                      className={
                        "absolute left-3 top-1/2 -translate-y-1/2 text-sm "
                        + "text-espresso-light"
                      }
                    >
                      ₹
                    </span>
                    <input
                      id={`delivery-fee-${order.order_number}`}
                      type="number"
                      min="0"
                      max="10000"
                      step="0.01"
                      inputMode="decimal"
                      value={deliveryFee}
                      onChange={(event) => setDeliveryFee(event.target.value)}
                      data-testid={`order-details-delivery-fee-input-${order.order_number}`}
                      className={
                        "w-full border border-line bg-ivory-100 py-3 pl-7 pr-3 text-base "
                        + "text-espresso outline-none focus:border-gold"
                      }
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={saveDeliveryFee}
                  disabled={savingDeliveryFee}
                  data-testid={`order-details-delivery-fee-save-${order.order_number}`}
                  className="btn-espresso disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingDeliveryFee ? "Saving…" : "Save charge"}
                </button>
              </div>
            </section>
          )}

          {isDelivery && (
            <section className="border-t border-line pt-5">
              <div className="flex items-baseline justify-between gap-3">
                <label
                  htmlFor={`actual-delivery-cost-${order.order_number}`}
                  className="label-xs text-espresso-light"
                >
                  Estimated actual delivery cost
                </label>
                <span className="text-[10px] uppercase tracking-[0.14em] text-emerald-800">
                  Margin only
                </span>
              </div>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="w-full sm:max-w-[180px]">
                  <div className="relative">
                    <span
                      className={
                        "absolute left-3 top-1/2 -translate-y-1/2 text-sm "
                        + "text-espresso-light"
                      }
                    >
                      ₹
                    </span>
                    <input
                      id={`actual-delivery-cost-${order.order_number}`}
                      type="number"
                      min="0"
                      max="10000"
                      step="0.01"
                      inputMode="decimal"
                      value={actualDeliveryCost}
                      onChange={(event) => setActualDeliveryCost(event.target.value)}
                      data-testid={`order-details-actual-delivery-cost-input-${order.order_number}`}
                      className={
                        "w-full border border-line bg-ivory-100 py-3 pl-7 pr-3 text-base "
                        + "text-espresso outline-none focus:border-gold"
                      }
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={saveActualDeliveryCost}
                  disabled={savingActualDeliveryCost}
                  data-testid={`order-details-actual-delivery-cost-save-${order.order_number}`}
                  className="btn-espresso disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingActualDeliveryCost ? "Saving…" : "Save cost"}
                </button>
              </div>
            </section>
          )}

          <section className="border-t border-line pt-5">
            <div className="flex items-center gap-2 label-xs text-espresso-light">
              <ShoppingBag size={14} aria-hidden="true" />
              Items
            </div>
            <div className="mt-3 space-y-2 text-sm text-espresso">
              {(order.items || []).map((item, index) => (
                <div
                  key={`${item.product_id || item.name}-${index}`}
                  className="flex justify-between gap-4"
                >
                  <span>{item.name}{item.flavor ? ` · ${item.flavor}` : ""} × {item.quantity}</span>
                  <span>₹{Number(item.subtotal || 0).toFixed(0)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-line pt-4">
              <span className="label-xs text-espresso-light">Total · {order.payment_status}</span>
              <strong
                data-testid={`order-details-total-${order.order_number}`}
                className="font-serif text-2xl text-espresso"
              >
                ₹{Number(order.total || 0).toFixed(0)}
              </strong>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}